import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Tag } from "lucide-react";

/**
 * OffersBanner — slim horizontal ticker of currently-active offers.
 * Sticker-style OFFERS tag on the left, smooth marquee on the right.
 */
export default function OffersBanner({ audience = "customer", className = "" }) {
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    api
      .get("/offers", { params: { audience } })
      .then(({ data }) => setOffers(data || []))
      .catch(() => setOffers([]));
  }, [audience]);

  if (offers.length === 0) return null;

  // Duplicate the list ≥3x so the loop is always visibly scrolling even with
  // 1–2 offers, and the seam between A→B→A is invisible.
  const minCopies = offers.length < 3 ? 4 : 2;
  const loop = Array.from({ length: minCopies }).flatMap(() => offers);

  // Slower, easier-to-read scroll speed (~ 22s per copy regardless of count).
  const durationSec = Math.max(22, loop.length * 4);

  return (
    <div
      className={`mb-6 rounded-2xl bg-gradient-to-r from-[#FFF6EA] via-[#FCEBD8] to-[#FFF6EA] border border-amber-200/70 shadow-sm overflow-hidden ${className}`}
      data-testid={`offers-banner-${audience}`}
    >
      <div className="flex items-stretch h-11">
        {/* Left sticker tag */}
        <div className="shrink-0 flex items-center justify-center gap-1.5 px-3 sm:px-4 bg-amber-500 text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.16em]">
          <Sparkles className="h-3.5 w-3.5 shrink-0" strokeWidth={2.4} />
          <span className="hidden sm:inline">Offers for you</span>
          <span className="sm:hidden">Offers</span>
        </div>

        {/* Marquee track */}
        <div className="relative flex-1 overflow-hidden h-full">
          {/* Soft fade-edges so text gently enters/exits */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-[#FFF6EA] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-[#FFF6EA] to-transparent z-10" />

          <div
            className="absolute inset-y-0 left-0 flex items-center gap-10 whitespace-nowrap animate-marquee hover:[animation-play-state:paused]"
            style={{ animationDuration: `${durationSec}s` }}
          >
            {loop.map((o, i) => {
              const isPercent = o.discount_type === "percent";
              const off = isPercent
                ? `${o.discount_value}% OFF`
                : `$${o.discount_value} OFF`;
              return (
                <span
                  key={`${o.offer_id}-${i}`}
                  className="inline-flex items-center gap-2.5 text-[13px] sm:text-sm text-stone-800 leading-none"
                  data-testid={i < offers.length ? `offer-item-${o.offer_id}` : undefined}
                >
                  <Tag className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                  <span className="font-semibold">{o.title}</span>
                  <span className="px-2 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold tracking-wide">
                    {off}
                  </span>
                  {o.code && (
                    <span className="text-[11px] text-stone-500">
                      code{" "}
                      <span className="font-mono font-semibold text-stone-700">
                        {o.code}
                      </span>
                    </span>
                  )}
                  <span className="text-amber-400 select-none">•</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
